import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faUserGraduate, faTriangleExclamation, faUserSlash } from '@fortawesome/free-solid-svg-icons';
import StudentClasses from './StudentClasses';
import './StudentProfile.css';

const API = process.env.REACT_APP_API_BASE_URL;

const BackLink = () => (
  <Link to="/teacher/MyStudents" className="rmtsp-back">
    <FontAwesomeIcon icon={faArrowLeft} /> My Students
  </Link>
);

const StudentProfile = () => {
  const { studentId } = useParams();
  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reload, setReload] = useState(0);
  const [photoBroken, setPhotoBroken] = useState(false);

  // ---------- Load (same endpoint) ----------
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    setPhotoBroken(false);
    (async () => {
      try {
        const res = await axios.get(`${API}/api/teacher/studentprofile/${studentId}`);
        if (!cancelled) setStudent(res.data?.student || null);
      } catch (err) {
        console.error('Error fetching student profile:', err);
        if (!cancelled) setError('We couldn’t load this student’s profile.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [studentId, reload]);

  if (loading) {
    return (
      <div className="rmtsp" aria-busy="true">
        <div className="rmtsp-inner">
          <div className="rmtsp-skel rmtsp-skel--back" />
          <div className="rmtsp-skel rmtsp-skel--hero" />
          <div className="rmtsp-skel rmtsp-skel--body" />
        </div>
      </div>
    );
  }

  if (error || !student) {
    return (
      <div className="rmtsp">
        <div className="rmtsp-inner">
          <BackLink />
          <div className="rmtsp-empty">
            <FontAwesomeIcon icon={error ? faTriangleExclamation : faUserSlash} className={error ? 'is-error' : ''} />
            <h2>{error ? 'Something went wrong' : 'Student not found'}</h2>
            <p>{error || 'This profile doesn’t exist or is no longer linked to you.'}</p>
            {error && (
              <button type="button" className="rmtsp-btn" onClick={() => setReload((n) => n + 1)}>
                Try again
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  const name = [student.FirstName, student.LastName].filter(Boolean).join(' ');

  return (
    <div className="rmtsp">
      <div className="rmtsp-inner">
        <BackLink />

        {/* ---------- Student card ---------- */}
        <section className="rmtsp-hero" aria-label="Student">
          <span className="rmtsp-photo">
            {student.Image && !photoBroken ? (
              <img src={student.Image} alt="" onError={() => setPhotoBroken(true)} />
            ) : (
              <FontAwesomeIcon icon={faUserGraduate} />
            )}
          </span>
          <div className="rmtsp-who">
            <span className="rmtsp-eyebrow">Student profile</span>
            <h1>{name || 'Student'}</h1>
            <ul className="rmtsp-facts">
              {student.Grade != null && (
                <li>
                  <span>Grade</span>
                  <strong>{student.Grade}</strong>
                </li>
              )}
              {student.Age != null && (
                <li>
                  <span>Age</span>
                  <strong>{student.Age}</strong>
                </li>
              )}
            </ul>
          </div>
        </section>

        {/* ---------- Classes (same component + same prop) ---------- */}
        <div className="rmtsp-content">
          <StudentClasses StudentId={studentId} />
        </div>
      </div>
    </div>
  );
};

export default StudentProfile;